/**
 * Card Component - Versatile card with header, body, footer sections
 */

import React from 'react';
import { colors, spacing, borderRadius, shadows } from '../../styles/design-tokens';

const Card = React.forwardRef(({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
  header = null,
  footer = null,
  hoverable = false,
  loading = false,
  ...props
}, ref) => {
  const variants = {
    default: 'bg-surface border border-neutral-200',
    elevated: 'bg-surface border border-neutral-200 shadow-card',
    outline: 'bg-transparent border-2 border-neutral-200',
    ghost: 'bg-transparent border-transparent',
  };

  const paddings = {
    none: '',
    sm: 'px-4 py-3',
    md: 'px-6 py-5',
    lg: 'px-8 py-7',
    xl: 'px-10 py-9',
  };

  const baseStyles = `
    rounded-xl
    overflow-hidden
    transition-all duration-200
    ${variants[variant] || variants.default}
    ${paddings[padding] || paddings.md}
    ${hoverable ? 'hover:border-brand hover:shadow-lg hover:-translate-y-0.5 cursor-pointer' : ''}
    ${loading ? 'opacity-60 pointer-events-none' : ''}
    ${className}
  `;

  // Skeleton loader
  if (loading) {
    return (
      <div ref={ref} className={baseStyles} {...props}>
        <div className="space-y-3">
          <div className="h-4 w-2/3 bg-neutral-100 rounded shimmer" />
          <div className="h-3 w-full bg-neutral-100 rounded shimmer" />
          <div className="h-3 w-4/5 bg-neutral-100 rounded shimmer" />
          <div className="h-8 w-1/3 bg-neutral-100 rounded shimmer" />
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={baseStyles} {...props}>
      {header && (
        <div className="border-b border-neutral-200 pb-4 mb-4">
          {typeof header === 'string' ? (
            <h3 className="text-lg font-semibold text-ink">{header}</h3>
          ) : (
            header
          )}
        </div>
      )}
      <div className="flex-1">{children}</div>
      {footer && (
        <div className="border-t border-neutral-200 pt-4 mt-4">
          {footer}
        </div>
      )}
    </div>
  );
});

Card.displayName = 'Card';

export default Card;